import axios from "@utils/axios-instance";

/**
 * Global search (GET /search) — grouped results across leads, deals and
 * recordings, consumed by the top-bar command palette. Every item carries
 * `ref` (string, always defined): the homogeneous identifier for building
 * the details URL (lead id / deal id / recording sessionId).
 */

export type TSearchStatus = {
  label: string;
  color: string;
};

export type TSearchLeadItem = {
  type: "lead";
  id: number;
  ref: string;
  title: string;
  subtitle?: string;
  status?: TSearchStatus | null;
  meta?: {
    email?: string;
    phone?: string;
    source?: string;
    rating?: number;
    created?: string;
  };
};

export type TSearchDealItem = {
  type: "deal";
  id: number;
  ref: string;
  title: string;
  subtitle?: string;
  status?: TSearchStatus | null;
  meta?: {
    amount?: number;
    currency?: string;
    ownerName?: string;
    pipelineName?: string;
    stageName?: string;
    leadId?: number;
    leadName?: string;
    closeDate?: string;
    created?: string;
  };
};

export type TSearchRecordingItem = {
  type: "recording";
  id: number;
  ref: string;
  title: string;
  subtitle?: string;
  status?: TSearchStatus | null;
  meta?: {
    channel?: string;
    durationSeconds?: number;
    startDate?: string;
    agentName?: string;
    leadName?: string;
    leadRating?: number;
    outcome?: string;
    provider?: string;
    sessionId?: string;
  };
};

export type TSearchGroup<T> = {
  total: number;
  items: T[];
};

export type TSearchResponse = {
  leads: TSearchGroup<TSearchLeadItem>;
  deals: TSearchGroup<TSearchDealItem>;
  recordings: TSearchGroup<TSearchRecordingItem>;
};

export const globalSearch = async (query: string, limit = 6): Promise<TSearchResponse> => {
  const response = await axios.get(`/search`, { params: { query, limit } });
  return response.data;
};
